import React, { useState } from "react";

import { Dialog, DialogContent, Label, Select } from "@calcom/ui";

import { PaymentOptions } from "@lib/constant";

import { XmarkIcon } from "@components/Icons";
import { useOfficeContext } from "@components/navbar";
import BatchPaymentRequest from "@components/patients/batchPaymentRequest";
import InsurancePayment from "@components/patients/insurance";
import PaymentRequest from "@components/patients/paymentRequest";
import TerminalPayment from "@components/patients/terminalPayment";
import UnreferencedRefund from "@components/patients/unreferenced-refund";
import VirtualPayment from "@components/patients/virtualPayment";

const payrixApiKey = process.env.PAYRIXPUBLICAPIKEY || "";
const payrixPaymentEnv = process.env.PAYRIXENVIRONMENT || "";

type CreatePaymentDlgProps = {
  open: boolean;
  handler: React.Dispatch<React.SetStateAction<boolean>>;
  patientId?: number;
  officeId: number;
  appId: string;
  env: string;
  defaultTab?: TabTitle;
  createConversationRoom?: boolean;
  selectedPatientIds?: number[];
};

type TabTitle =
  | "virtual"
  | "terminal"
  | "insurance"
  | "request"
  | "batch-request"
  | "link"
  | "paymentplan"
  | "subscription"
  | "unreferenced-refund";

export default function CreatePaymentDialog(props: CreatePaymentDlgProps) {
  const {
    open,
    handler,
    patientId,
    officeId,
    appId,
    env,
    defaultTab,
    createConversationRoom,
    selectedPatientIds,
  } = props;

  const { office } = useOfficeContext();

  const [activeTab, setActiveTab] = useState<TabTitle>(defaultTab || "terminal");

  const isPayrix = office?.paymentGateway === "PAYRIX";
  const filteredPaymentOptions = isPayrix
    ? PaymentOptions.filter((o) => o.value !== "unreferenced-refund")
    : PaymentOptions;

  return (
    <Dialog open={open} onOpenChange={handler}>
      <DialogContent
        className="min-w-screen min-h-screen rounded-none p-0 py-2"
        enableOverflow
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}>
        <div className="relative min-h-screen">
          <div className="flex h-[44px] w-full items-center border-b px-8 pb-2">
            <span className="text-xl font-medium">Create Payment</span>
            <div className="flex-grow" />
            <div className="flex items-center gap-4">
              <XmarkIcon className="cursor-pointer" onClick={() => handler(false)} />
            </div>
          </div>
          <div className="relative mx-auto max-w-2xl pt-4">
            <div className="space-y-6">
              <div className="p-4 pt-0 md:p-8 md:pt-0">
                <div id="payment-type-select">
                  <Label>Payment Type</Label>
                  <Select
                    options={filteredPaymentOptions}
                    value={filteredPaymentOptions.find((p) => p.value === activeTab)}
                    onChange={(option) => {
                      if (option) {
                        setActiveTab(option.value as TabTitle);
                      }
                    }}
                  />
                </div>
                <div id="payment-type-content">
                  {activeTab === "virtual" ? (
                    <VirtualPayment
                      patientId={patientId}
                      officeId={officeId}
                      appId={appId}
                      env={env}
                      setPaymentDialog={handler}
                      payrixApiKey={payrixApiKey}
                      payrixPaymentEnv={payrixPaymentEnv}
                    />
                  ) : activeTab === "terminal" ? (
                    <TerminalPayment patientId={patientId} officeId={officeId} setPaymentDialog={handler} />
                  ) : activeTab === "insurance" ? (
                    <InsurancePayment
                      patientId={patientId}
                      officeId={officeId}
                      appId={appId}
                      env={env}
                      setPaymentDialog={handler}
                      payrixApiKey={payrixApiKey}
                      payrixPaymentEnv={payrixPaymentEnv}
                    />
                  ) : activeTab === "request" ? (
                    <PaymentRequest
                      patientId={patientId}
                      officeId={officeId}
                      officename={office?.officename}
                      setPaymentDialog={handler}
                      createConversationRoom={createConversationRoom}
                    />
                  ) : activeTab === "unreferenced-refund" ? (
                    <UnreferencedRefund
                      patientId={patientId}
                      officeId={officeId}
                      setPaymentDialog={handler}
                    />
                  ) : activeTab === "batch-request" ? (
                    <BatchPaymentRequest
                      selectedPatientIds={selectedPatientIds}
                      officeId={officeId}
                      setPaymentDialog={handler}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
